import { useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';

// Teams PR A: the JS twin of public.integration_enabled_for_me(key). Every built-in integration (Teams now; Slack, Authorize.net,
// Google Workspace later) is a sealed package with four states:
//   not_offered -> opted_in (Admin Center tile only) -> on (assigned people only) -> off (gone again for staff).
// The database is the real lock: every table policy and every server function asks the SQL gate, and refuses. This helper only
// decides what a screen renders, and it fails closed: any error, a missing answer or a loading answer means "nothing".

export const INTEGRATION_KEYS = Object.freeze({
    MICROSOFT_TEAMS: 'integrations.microsoft_teams',
});

export const INTEGRATION_STATES = Object.freeze(['not_offered', 'opted_in', 'on', 'off']);

export const CLOSED_ACCESS = Object.freeze({ state: 'not_offered', enabledForMe: false, canManage: false, loading: false, error: '' });

/** Normalize whatever public.integration_access(key) returned. Anything unexpected is treated as closed. */
export function normalizeAccess(data) {
    if (!data || typeof data !== 'object') return { ...CLOSED_ACCESS };
    const state = INTEGRATION_STATES.includes(data.state) ? data.state : 'not_offered';
    return {
        state,
        enabledForMe: data.enabled_for_me === true,
        canManage: data.can_manage === true && state !== 'not_offered',
        loading: false,
        error: '',
    };
}

/** Ask the database. Never throws: a failure is reported as closed with the error text. */
export async function fetchIntegrationAccess(client, key) {
    try {
        const { data, error } = await client.rpc('integration_access', { p_key: key });
        if (error) return { ...CLOSED_ACCESS, error: error.message || 'Could not check this integration.' };
        return normalizeAccess(data);
    } catch (failure) {
        return { ...CLOSED_ACCESS, error: failure?.message || 'Could not check this integration.' };
    }
}

/** May this person use the integration on a staff screen right now? (Loading = no, so nothing flashes on and off.) */
export function canUseIntegration(access) {
    return Boolean(access && !access.loading && access.enabledForMe === true && access.state === 'on');
}

/** Should the Admin Center show this integration's tile? Opted in (or on / off again) and the person can manage it. */
export function canManageIntegration(access) {
    return Boolean(access && !access.loading && access.canManage === true && access.state !== 'not_offered');
}

/** React hook. userId keys the lookup so a sign-out / sign-in re-asks. */
export function useIntegrationAccess(key, userId, client = supabaseClient) {
    const [access, setAccess] = useState(() => ({ ...CLOSED_ACCESS, loading: Boolean(userId) }));
    const [tick, setTick] = useState(0);

    useEffect(() => {
        let active = true;
        if (!userId || !key) {
            setAccess({ ...CLOSED_ACCESS });
            return () => { active = false; };
        }
        setAccess((previous) => ({ ...previous, loading: true }));
        fetchIntegrationAccess(client, key).then((next) => { if (active) setAccess(next); });
        return () => { active = false; };
    }, [client, key, userId, tick]);

    return { ...access, refresh: () => setTick((value) => value + 1) };
}
